import { useRouter, useSearchParams } from 'next/navigation'

import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'sonner'

import { ProfileSchema } from '@/components/forms/profile/form-options'

import { fetcher, signIn } from '@/lib'
import { useAdminStore } from '@/lib/auth/admin-store'
import { Admin, ClientLoginResponse, LoginRequest } from '@/lib/auth/auth-types'
import { extractUserPermissions } from '@/lib/auth/permissions-utils'
import { ENDPOINTS } from '@/query-client'

export function useLoginMutation() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const redirectPath = searchParams.get('redirect')
  const setAdmin = useAdminStore((state) => state.setAdmin)
  const setAdminPermissions = useAdminStore(
    (state) => state.setAdminPermissions
  )
  return useMutation<
    API<ClientLoginResponse | null>,
    AxiosError<API>,
    LoginRequest
  >({
    meta: {
      toastOnError: false,
      toastOnSuccess: false
    },
    mutationFn: async (data) => await signIn(data),
    onSuccess: (data) => {
      if (!data.success) toast.error(data.message)
      if (data.success && data.data) {
        setAdmin(data.data.admin)
        setAdminPermissions(extractUserPermissions(data.data.admin.role))
        toast.success(data.message)
        router.push(redirectPath || '/')
      }
    }
  })
}

export function useUpdateLanguageMutation() {
  return useMutation<API<Admin>, AxiosError<API>, { language: 'ar' | 'en' }>({
    meta: {
      invalidatesQuery: [['current-admin'], ['current-user']],
      toastOnSuccess: false
    },
    mutationFn: async (data) =>
      await fetcher(ENDPOINTS.AUTH.UPDATE_LANGUAGE, {
        auth: true,
        body: data,
        method: 'POST'
      })
  })
}

export function useUpdateProfileMutation() {
  return useMutation<API<Admin>, AxiosError<API>, ProfileSchema>({
    meta: {
      invalidatesQuery: [['current-admin'], ['current-user']]
    },
    mutationFn: async (data) =>
      await fetcher(ENDPOINTS.AUTH.UPDATE_INFO, {
        auth: true,
        body: data,
        method: 'POST'
      })
  })
}

export const useForgotPasswordMutation = () => {
  return useMutation<API<void>, AxiosError<API>, { email: string }>({
    mutationFn: async (data) =>
      await fetcher(ENDPOINTS.AUTH.FORGOT_PASSWORD, {
        body: data,
        method: 'POST'
      })
  })
}

export const useResendOtpMutation = () => {
  return useMutation<API<void>, AxiosError<API>, { email: string }>({
    mutationFn: async (data) =>
      await fetcher(ENDPOINTS.AUTH.RESEND_OTP, {
        body: data,
        method: 'POST'
      })
  })
}

export const useVerifyOtpMutation = () => {
  return useMutation<
    API<void>,
    AxiosError<API>,
    { code: string; email: string }
  >({
    mutationFn: async (data) =>
      await fetcher(ENDPOINTS.AUTH.VERIFY_OTP, {
        body: data,
        method: 'POST'
      })
  })
}

export const useResetPasswordMutation = () => {
  return useMutation<
    API<void>,
    AxiosError<API>,
    {
      code: string
      email: string
      password: string
      password_confirmation: string
    }
  >({
    mutationFn: async (data) =>
      await fetcher(ENDPOINTS.AUTH.RESET_PASSWORD, {
        body: data,
        method: 'POST'
      })
  })
}
